import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase/config';
import { updateUsername as updateUsernameCallable } from '../../../services/firebase/functions';

const RABBIT_POOLS = {
  colors: ['yellow','coral','indigo','mint','slate','ivory','crimson','amber'],
  moods: ['empathic','restless','calm','electric','dreamy','fierce','soft','bold'],
  textures: ['fuzzy','smooth','glossy','matte','woven','velvet'],
  nature: ['midnight','dawn','dusk','forest','cloud','shadow','sunlit']
};

const ALL_RABBIT_ADJ = [
  ...RABBIT_POOLS.colors,
  ...RABBIT_POOLS.moods,
  ...RABBIT_POOLS.textures,
  ...RABBIT_POOLS.nature
];

// Basic profanity filter - catches common English profanities and reserved words.
// This is a client-side guard; it is not exhaustive.
const PROFANITY_LIST = [
  'admin', 'root', 'support', 'help', 'system', 'moderator', 'mod',
  'shit', 'fuck', 'bitch', 'asshole', 'damn', 'crap', 'piss', 'slut',
  'whore', 'dick', 'cock', 'pussy', 'nigger', 'nigga', 'fag', 'faggot',
  'retard', 'cunt', 'kike', 'chink', 'spic', 'wetback', 'dyke'
];

const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;
const USERNAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

interface UsernameValidationResult {
  valid: boolean;
  error?: string;
}

interface UsernameUpdateResult {
  success: boolean;
  error?: string;
}

const normalizeUsername = (username: string): string => {
  return username.trim().toLowerCase();
};

const getRandomItem = <T>(arr: T[]): T => {
  return arr[Math.floor(Math.random() * arr.length)];
};

const getRandomNumber = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const validateUsername = (username: string): UsernameValidationResult => {
  const normalized = normalizeUsername(username);

  if (!normalized) {
    return { valid: false, error: 'Username is required' };
  }

  if (normalized.length < USERNAME_MIN_LENGTH) {
    return { valid: false, error: `Username must be at least ${USERNAME_MIN_LENGTH} characters` };
  }

  if (normalized.length > USERNAME_MAX_LENGTH) {
    return { valid: false, error: `Username must be at most ${USERNAME_MAX_LENGTH} characters` };
  }

  if (!USERNAME_PATTERN.test(normalized)) {
    return { valid: false, error: 'Username can only contain letters, numbers, hyphens and underscores' };
  }

  if (PROFANITY_LIST.some((word) => normalized.includes(word))) {
    return { valid: false, error: 'This username is not allowed' };
  }

  return { valid: true };
};

export const isUsernameAvailable = async (username: string): Promise<boolean> => {
  const normalized = normalizeUsername(username);
  const validation = validateUsername(normalized);
  if (!validation.valid) return false;

  const usernameDoc = await getDoc(doc(db, 'usernames', normalized));
  return !usernameDoc.exists();
};

export const generateRandomUsername = async (
  maxAttempts = 20
): Promise<string> => {
  for (let i = 0; i < maxAttempts; i++) {
    const adj = getRandomItem(ALL_RABBIT_ADJ);
    const candidate = i < 10
      ? `${adj}-rabbit`
      : `${adj}-rabbit-${getRandomNumber(1, 99)}`;

    try {
      const isAvailable = await isUsernameAvailable(candidate);
      if (isAvailable) {
        return candidate;
      }
    } catch {
      // En cas d'erreur de lecture (rules/réseau), on sort et on fallback numérique
      break;
    }
  }
  return `rabbit-${getRandomNumber(100000, 999999)}`;
};

export const updateUsername = async (
  newUsername: string
): Promise<UsernameUpdateResult> => {
  const normalized = normalizeUsername(newUsername);
  const validation = validateUsername(normalized);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  try {
    const result = await updateUsernameCallable({ username: normalized });

    const data = result.data as { success: boolean; username?: string };
    if (data.success) {
      return { success: true };
    }
    return { success: false, error: 'Failed to update username' };
  } catch (error: any) {
    if (error?.code === 'functions/already-exists') {
      return { success: false, error: 'This username is already taken' };
    }
    return { success: false, error: error?.message || 'Failed to update username' };
  }
};
